import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { toast } from "sonner";
import { 
  Settings,
  Calendar,
  FileText,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronLeft,
  Check,
  X,
  Menu
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

export default function RHSettings() {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({ name: "", paid: true, max_days: "", description: "" });
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [showNewHolidayDialog, setShowNewHolidayDialog] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [typesRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/admin/leave-types`, getAuthHeader()),
        axios.get(`${API}/admin/holidays?year=${selectedYear}`, getAuthHeader())
      ]);
      setLeaveTypes(typesRes.data);
      setHolidays(holidaysRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  // Leave Types handlers
  const handleCreateType = async () => {
    try {
      await axios.post(`${API}/admin/leave-types`, {
        ...newType,
        max_days: newType.max_days ? parseInt(newType.max_days) : null
      }, getAuthHeader());
      toast.success("Type de congé créé");
      setShowNewTypeDialog(false);
      setNewType({ name: "", paid: true, max_days: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleUpdateType = async (typeId, updates) => {
    try {
      await axios.put(`${API}/admin/leave-types/${typeId}`, updates, getAuthHeader());
      toast.success("Type de congé mis à jour");
      setEditingType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleToggleType = async (typeId, active) => {
    try {
      await axios.put(`${API}/admin/leave-types/${typeId}`, { active }, getAuthHeader());
      toast.success(active ? "Type activé" : "Type désactivé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Holidays handlers
  const handleCreateHoliday = async () => {
    try {
      await axios.post(`${API}/admin/holidays`, newHoliday, getAuthHeader());
      toast.success("Jour férié ajouté");
      setShowNewHolidayDialog(false);
      setNewHoliday({ date: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleToggleHoliday = async (holidayId, active) => {
    try {
      await axios.put(`${API}/admin/holidays/${holidayId}`, { active }, getAuthHeader());
      toast.success(active ? "Jour férié activé" : "Jour férié désactivé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleRegenerateHolidays = async () => {
    if (!confirm(`Régénérer tous les jours fériés pour ${selectedYear} ? Les modifications manuelles seront perdues.`)) {
      return;
    }
    try {
      await axios.post(`${API}/admin/holidays/generate/${selectedYear}`, {}, getAuthHeader());
      toast.success(`Jours fériés ${selectedYear} régénérés`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la régénération");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="hidden lg:block"><Sidebar /></div>
        <main className="flex-1 bg-gray-50 p-4 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar />
        </SheetContent>
      </Sheet>

      <main className="flex-1 bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center gap-2 lg:gap-4">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/rh')} className="hidden lg:flex">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Paramètres
              </h1>
              <p className="text-gray-500 text-sm hidden sm:block">Types de congés et jours fériés</p>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          <Tabs defaultValue="leave-types" className="space-y-4 lg:space-y-6">
            <TabsList className="bg-white border shadow-sm">
              <TabsTrigger value="leave-types" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm">
                <FileText className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Types de congés</span>
                <span className="sm:hidden">Types</span>
              </TabsTrigger>
              <TabsTrigger value="holidays" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm">
                <Calendar className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Jours fériés</span>
                <span className="sm:hidden">Fériés</span>
              </TabsTrigger>
            </TabsList>

            {/* Leave Types */}
            <TabsContent value="leave-types">
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">Types de congés</CardTitle>
                    <Dialog open={showNewTypeDialog} onOpenChange={setShowNewTypeDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#00A3E0] hover:bg-[#0090c7] text-white">
                          <Plus className="h-4 w-4 mr-2" />
                          Nouveau type
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Nouveau type de congé</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Nom *</Label>
                            <Input
                              value={newType.name}
                              onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                              placeholder="Ex: Congé déménagement"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Durée maximale (jours)</Label>
                            <Input
                              type="number"
                              value={newType.max_days}
                              onChange={(e) => setNewType({ ...newType, max_days: e.target.value })}
                              placeholder="Laisser vide si illimité"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                              value={newType.description}
                              onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                              placeholder="Description affichée au collaborateur"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label>Rémunéré</Label>
                            <Switch
                              checked={newType.paid}
                              onCheckedChange={(checked) => setNewType({ ...newType, paid: checked })}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowNewTypeDialog(false)}>
                            Annuler
                          </Button>
                          <Button onClick={handleCreateType} className="bg-[#00A3E0] hover:bg-[#0090c7] text-white">
                            Créer
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Nom</TableHead>
                          <TableHead>Durée max</TableHead>
                          <TableHead>Rémunéré</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Actif</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leaveTypes.map((type) => (
                          <TableRow key={type.id} className={!type.active ? 'opacity-50' : ''}>
                            <TableCell className="font-medium">{type.name}</TableCell>
                            <TableCell>
                              {type.max_days ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {type.max_days} j
                                </Badge>
                              ) : (
                                <span className="text-gray-400">Illimité</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {type.paid ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <Check className="h-3 w-3 mr-1" />
                                  Oui
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  <X className="h-3 w-3 mr-1" />
                                  Non
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                              {type.description}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={type.active}
                                onCheckedChange={(checked) => handleToggleType(type.id, checked)}
                                disabled={type.id === 'cp'}
                              />
                            </TableCell>
                            <TableCell>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => setEditingType(type)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Modifier {type.name}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label>Nom</Label>
                                      <Input
                                        defaultValue={type.name}
                                        onChange={(e) => setEditingType({ ...type, name: e.target.value })}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Durée maximale (jours)</Label>
                                      <Input
                                        type="number"
                                        defaultValue={type.max_days || ""}
                                        onChange={(e) => setEditingType({ ...type, max_days: e.target.value ? parseInt(e.target.value) : null })}
                                        placeholder="Laisser vide si illimité"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Description</Label>
                                      <Input
                                        defaultValue={type.description}
                                        onChange={(e) => setEditingType({ ...type, description: e.target.value })}
                                      />
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <Label>Rémunéré</Label>
                                      <Switch
                                        defaultChecked={type.paid}
                                        onCheckedChange={(checked) => setEditingType({ ...type, paid: checked })}
                                      />
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button 
                                      onClick={() => handleUpdateType(type.id, {
                                        name: editingType?.name,
                                        max_days: editingType?.max_days,
                                        description: editingType?.description,
                                        paid: editingType?.paid
                                      })}
                                      className="bg-[#00A3E0] hover:bg-[#0090c7] text-white"
                                    >
                                      Enregistrer
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Holidays */}
            <TabsContent value="holidays">
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <CardTitle className="text-lg font-semibold">Jours fériés</CardTitle>
                      <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026, 2027, 2028].map((year) => (
                            <SelectItem key={year} value={String(year)}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleRegenerateHolidays}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Régénérer
                      </Button>
                      <Dialog open={showNewHolidayDialog} onOpenChange={setShowNewHolidayDialog}>
                        <DialogTrigger asChild>
                          <Button className="bg-[#00A3E0] hover:bg-[#0090c7] text-white">
                            <Plus className="h-4 w-4 mr-2" />
                            Ajouter
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Ajouter un jour férié</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Date *</Label>
                              <Input
                                type="date"
                                value={newHoliday.date}
                                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Nom *</Label>
                              <Input
                                value={newHoliday.name}
                                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                                placeholder="Ex: Journée de solidarité"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowNewHolidayDialog(false)}>
                              Annuler
                            </Button>
                            <Button onClick={handleCreateHoliday} className="bg-[#00A3E0] hover:bg-[#0090c7] text-white">
                              Ajouter
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Date</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Actif</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {holidays.map((holiday) => (
                          <TableRow key={holiday.id} className={!holiday.active ? 'opacity-50' : ''}>
                            <TableCell className="font-medium">
                              {format(new Date(holiday.date), "EEEE dd MMMM yyyy", { locale: fr })}
                            </TableCell>
                            <TableCell>{holiday.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                holiday.type === 'mobile' 
                                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                                  : holiday.type === 'custom'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-gray-100 text-gray-600 border-gray-200'
                              }>
                                {holiday.type === 'mobile' ? 'Mobile' : holiday.type === 'custom' ? 'Personnalisé' : 'Fixe'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={holiday.active}
                                onCheckedChange={(checked) => handleToggleHoliday(holiday.id, checked)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Info :</strong> Les jours fériés sont automatiquement exclus du calcul des CP. 
                  Les jours "mobiles" (Pâques, Ascension, Pentecôte) sont calculés automatiquement chaque année.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
